import { useQuery } from '@tanstack/react-query';
import { useAuth } from './use-auth';
import { getScreeningReminders } from '../services/health-intelligence-service';
import type { ScreeningReminder } from '../types/health-intelligence-types';

export const screeningKeys = {
  all: ['preventiveScreenings'] as const,
  list: (userId: string | undefined) => [...screeningKeys.all, userId] as const,
};

/**
 * Hook to fetch preventive screening reminders and track overdue screenings
 *
 * @returns Query result with screening reminders and computed metrics
 */
export const usePreventiveScreenings = () => {
  const { user } = useAuth();
  const userId = user?.id;

  const queryResult = useQuery<ScreeningReminder[]>({
    queryKey: screeningKeys.list(userId),
    queryFn: () => getScreeningReminders(userId!),
    enabled: !!userId,
    staleTime: 30 * 60 * 1000, // 30 minutes
  });

  const screenings = queryResult.data ?? [];

  // Computed values
  const overdueScreenings = screenings.filter((s) => s.isOverdue);
  const overdueCount = overdueScreenings.length;
  const nextScreening = screenings
    .filter((s) => !s.isOverdue)
    .sort(
      (a, b) =>
        new Date(a.recommendedDate).getTime() -
        new Date(b.recommendedDate).getTime()
    )[0];

  return {
    ...queryResult,
    screenings,
    overdueScreenings,
    overdueCount,
    nextScreening,
  };
};
